import type { ReadonlyTransaction, Transaction } from '@atlaskit/editor-prosemirror/state';

import { createInitialState } from './initialState';
import { mapStartPos, mapToPos } from './transaction-builders/positionMapping';
import { ACTIONS } from './transforms';
import type { PluginMeta, PurePmInEditorDirectStreamingPluginState, StreamPhase } from './types';
import { clampPosition } from './utils/positionValidation';

/**
 * Applies plugin state transformations based on action type.
 * Uses discriminated union for proper type narrowing in each case.
 *
 * @param meta - The action metadata (discriminated union)
 * @param pluginState - Current plugin state
 * @returns Updated plugin state, or undefined if no change
 */
export function applyPlugin(
	meta: PluginMeta,
	pluginState: PurePmInEditorDirectStreamingPluginState,
): PurePmInEditorDirectStreamingPluginState | undefined {
	switch (meta?.action) {
		case ACTIONS.INIT_POSITIONS: {
			const {
				start,
				end,
				showHighlight,
				showAIContentHighlight,
				showTelepointer,
				telepointerPos,
				originalUserContentSlice,
				streamStartPos,
				sessionId,
			} = meta;

			return {
				...createInitialState(),
				startPos: start,
				userContentEndPos: end,
				loadingPos: start,
				showHighlight,
				showAIContentHighlight,
				showTelepointer,
				telepointerPos,
				isStreaming: true,
				chunkNumber: 0,
				streamTimeStamp: Date.now(),
				streamPhase: 'mounting',
				isPaused: false,
				posAdjusted: false,
				hasFinalStream: false,
				currentSessionId: sessionId,
				...(originalUserContentSlice !== undefined && { originalUserContentSlice }),
				...(streamStartPos !== undefined && { streamStartPos }),
			};
		}
		case ACTIONS.ADJUST_INIT_POSITIONS:
			const { start, end } = meta;
			return {
				...pluginState,
				startPos: start,
				userContentEndPos: end,
			};

		case ACTIONS.UPDATE_LOADING_POSITIONS: {
			const { startPos, userContentEndPos, loadingPos } = pluginState;

			if (startPos === undefined || userContentEndPos === undefined) {
				return pluginState;
			}

			const max = userContentEndPos;
			const min = loadingPos ?? startPos;

			if (max <= min) {
				return pluginState;
			}

			// Randomize loading position within 1/8th to 3/8ths of range
			const eighth = (max - min) / 8;
			const low = Math.floor(min + eighth);
			const high = Math.ceil(min + 3 * eighth);
			const newLoadingPos = Math.ceil(Math.random() * (high - low) + low);

			return {
				...pluginState,
				loadingPos: newLoadingPos,
			};
		}

		case ACTIONS.UPDATE_STREAM_POSITIONS: {
			const {
				aiContentEndPos,
				telepointerPos,
				isFinalStream,
				lastProcessedVersion,
				aiContentSlice,
				scrollTargetPos,
				posAdjusted,
				startPos,
				userContentEndPos,
			} = meta.positions;

			const nextPhase: StreamPhase = isFinalStream ? 'finalizing' : 'streaming';

			let newState: PurePmInEditorDirectStreamingPluginState = {
				...pluginState,
				aiContentEndPos,
				telepointerPos,
				chunkNumber: pluginState.chunkNumber + 1,
				streamTimeStamp: Date.now(),
				loadingPos: undefined,
				streamPhase: nextPhase,
				// New fields (if provided)
				...(lastProcessedVersion !== undefined && { lastProcessedVersion }),
				...(aiContentSlice !== undefined && { aiContentSlice }),
				...(scrollTargetPos !== undefined && { scrollTargetPos }),
				...(posAdjusted !== undefined && { posAdjusted }),
				...(startPos !== undefined && { startPos }),
				...(userContentEndPos !== undefined && { userContentEndPos }),
			};

			if (isFinalStream) {
				newState = {
					...newState,
					isFinalStream: true,
					showHighlight: false,
					showAIContentHighlight: false,
					showTelepointer: false,
					isStreaming: false,
					isStreamingFinished: true,
					hasFinalStream: true,
					scrollTargetPos: undefined, // Clear scroll target on finalize
				};
			}

			return newState;
		}
		case ACTIONS.END_STREAM: {
			return {
				...createInitialState(),
				telepointerPos: pluginState.telepointerPos,
			};
		}
		default:
			return pluginState;
	}
}

/**
 * Maps all position fields in the plugin state when the document changes.
 * Ensures positions remain valid after document modifications.
 *
 * @param tr - The transaction that changed the document
 * @param pluginState - Current plugin state with positions to map
 * @returns Updated plugin state with mapped positions
 */
export function mapPositions(
	tr: ReadonlyTransaction | Transaction,
	pluginState: PurePmInEditorDirectStreamingPluginState,
): PurePmInEditorDirectStreamingPluginState {
	const docSize = tr.doc.content.size;

	return {
		...pluginState,
		...(pluginState.startPos !== undefined && {
			startPos: clampPosition(mapStartPos({ pos: pluginState.startPos, tr }), docSize),
		}),
		...(pluginState.userContentEndPos !== undefined && {
			userContentEndPos: clampPosition(
				mapToPos({ pos: pluginState.userContentEndPos, tr }),
				docSize,
			),
		}),
		...(pluginState.aiContentEndPos !== undefined && {
			aiContentEndPos: clampPosition(mapToPos({ pos: pluginState.aiContentEndPos, tr }), docSize),
		}),
		...(pluginState.loadingPos !== undefined && {
			loadingPos: clampPosition(tr.mapping.map(pluginState.loadingPos, -1), docSize),
		}),
		...(pluginState.telepointerPos !== undefined && {
			telepointerPos: clampPosition(tr.mapping.map(pluginState.telepointerPos, -1), docSize),
		}),
		...(pluginState.streamStartPos !== undefined && {
			streamStartPos: clampPosition(mapStartPos({ pos: pluginState.streamStartPos, tr }), docSize),
		}),
		...(pluginState.scrollTargetPos !== undefined && {
			scrollTargetPos: clampPosition(mapToPos({ pos: pluginState.scrollTargetPos, tr }), docSize),
		}),
	};
}
